import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KlUploadFileBasicComponent, type FileSelectResult } from './kl-upload-file-basic/kl-upload-file-basic';
import { KlUploadFileAdvancedComponent } from './kl-upload-file-advanced/kl-upload-file-advanced';
import { KlLabelComponent } from '../kl-label/kl-label';
import type { FileSelectEvent } from 'primeng/types/fileupload';
import { PrimeNG } from 'primeng/config';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

export interface IUploadedFile {
  name: string;
  size: number;
  type: string;
  content: string;
}

@Component({
  selector: 'kl-upload-file',
  standalone: true,
  imports: [ReactiveFormsModule, KlLabelComponent, KlUploadFileBasicComponent, KlUploadFileAdvancedComponent],
  templateUrl: './kl-upload-file.html',
  styleUrl: './kl-upload-file.scss',
})
export class KlUploadFileComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly primeConfig = inject(PrimeNG);

  private readonly advancedUpload = viewChild(KlUploadFileAdvancedComponent);
  private readonly basicUpload = viewChild(KlUploadFileBasicComponent);

  readonly formGroup = input.required<FormGroup>();
  readonly controlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');
  readonly disabled = input(false);

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  readonly maxFileSize = input(10);
  readonly acceptedFileExtentions = input('.pdf,.rar,.docx,image/*');
  readonly invalidFileTypeMessageSummary = input('Geçersiz belge türü: ');
  readonly invalidFileTypeMessageDetail = input('');
  readonly multiple = input(false);
  readonly mode = input<'basic' | 'advanced'>('basic');

  /** Prefill from parent (base64 / content string + metadata). */
  readonly fileContent = input('', { alias: 'fileContent' });
  readonly fileName = input('', { alias: 'fileName' });
  readonly fileType = input('', { alias: 'fileType' });

  readonly inputId = input('');
  readonly showFieldError = input(false);

  readonly selectEvent = output<IUploadedFile>();
  readonly clearEvent = output<void>();

  readonly selectedName = signal('');
  readonly selectedType = signal('');
  readonly selectedContent = signal('');
  readonly selectedSize = signal(0);
  readonly displayFileLabel = signal('');

  readonly invalidTypeDetailResolved = computed(() => {
    const d = this.invalidFileTypeMessageDetail();
    return d || 'İzin verilen dosya türleri: ' + this.acceptedFileExtentions();
  });

  readonly control = computed<AbstractControl | null>(() => this.formGroup().get(this.controlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bumped on every control event (see constructor) so
   * the validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);

  readonly fieldId = computed(() => (this.inputId() || this.controlName() || 'kl-upload-file').replace(/\s/g, '_'));

  readonly required = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.validator) return false;
    const v = c.validator({} as AbstractControl);
    return !!v?.['required'];
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const c = this.control();
    return !!(c && c.invalid && c.touched);
  });

  readonly controlError = computed(() => {
    this.controlState();
    const c = this.control();
    if (!c?.invalid || !c.touched) return null;
    if (c.errors?.['required']) return 'This field is required.';
    return null;
  });

  constructor() {
    effect(() => {
      const content = this.fileContent();
      const name = this.fileName();
      if (!content || !name) return;
      this.displayFileLabel.set(name);
      this.selectedContent.set(content);
    });

    effect((onCleanup) => {
      const sub = this.control()?.events.subscribe(() => this.controlState.update((v) => v + 1));
      onCleanup(() => sub?.unsubscribe());
    });
  }

  ngOnInit(): void {
    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);
  }

  private screenWidth(): number {
    return typeof document !== 'undefined' ? document.body.clientWidth : 1024;
  }

  onSelectedFile(event: FileSelectResult | FileSelectEvent): void {
    this.selectedType.set('');
    this.selectedName.set('');
    this.selectedContent.set('');
    this.selectedSize.set(0);

    const files = event.currentFiles;
    if (!files?.length) return;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const base64String = e.target?.result as string;
      if (!files.length) return;

      const f = files[0];
      this.selectedName.set(f.name);
      this.selectedType.set(f.type);
      this.selectedContent.set(base64String);
      this.selectedSize.set(f.size);
      this.displayFileLabel.set(this.getFileNameLabel(f));

      this.selectEvent.emit({
        name: f.name,
        size: f.size,
        type: f.type,
        content: base64String,
      });
    };

    reader.readAsDataURL(files[0]);
  }

  onClearFile(): void {
    this.selectedName.set('');
    this.selectedType.set('');
    this.selectedContent.set('');
    this.selectedSize.set(0);
    this.displayFileLabel.set('');
    this.control()?.setValue('', { emitEvent: false });
    this.clearEvent.emit();
  }

  /** Clears the selected file and bound form control (for parent-driven resets). */
  clear(): void {
    this.onClearFile();
    this.advancedUpload()?.clearUpload();
    const basic = this.basicUpload();
    if (basic) {
      basic.resetNativeInput();
    }
  }

  formatSize(bytes: number): string {
    const k = 1024;
    const dm = 3;
    const sizes = this.primeConfig.translation.fileSizeTypes;
    if (!sizes || sizes.length === 0) {
      return '0 B';
    }
    if (bytes === 0 && sizes) {
      return `0 ${sizes[0]}`;
    }

    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
    const unit = sizes[Math.max(0, i)] ?? 'B';
    return `${formattedSize} ${unit}`;
  }

  getFileNameLabel(file: File): string {
    let maxFileNameLength = 100;
    const screenSize = this.screenWidth();
    if (screenSize < 400) {
      maxFileNameLength = 10;
    }
    if (screenSize > 400 && screenSize < 800) {
      maxFileNameLength = 12;
    }
    if (file.name?.length > 0) {
      const splits = file.name.split('.');
      const fileNameSplits = splits.filter((split) => splits.indexOf(split) !== splits.length - 1).join('');
      if (fileNameSplits.length > maxFileNameLength) {
        return `${fileNameSplits.substring(0, maxFileNameLength) + '... .' + splits[splits.length - 1]} (${this.formatSize(file.size)})`;
      }
    }
    return `${file.name} (${this.formatSize(file.size)})`;
  }

  showFile(): void {
    // Dialog hook (legacy commented)
  }
}
